/**
 * Shared contract for the stage-media room capability.
 *
 * A room with `supportsSharedStageMedia: true` can host co-watched media
 * (YouTube, IPTV/HLS, MP4) on its stage. Hosts add/remove sources; stage
 * participants share transport control for all kinds: play/pause is
 * synchronized across all viewers for every transportable kind, while seek
 * is only meaningful for queueable VOD (YouTube/MP4). HLS is live-only and
 * rejects queue + seek operations (pause holds at the buffered frame; resume
 * snaps back to the live edge locally).
 *
 * Spec: _docs/Implementation/FunctionalSpecs/Pluggable-Rooms/iptv-townhall-record-stream-simulcast.md §6
 */

export const STAGE_MEDIA_KINDS = ['youtube', 'hls', 'mp4'] as const;
export type StageMediaKind = typeof STAGE_MEDIA_KINDS[number];

export interface StageMediaYouTubeSource {
    kind: 'youtube';
    videoId: string;
    title?: string;
    thumbnailUrl?: string;
    /** Original input URL preserved for round-trip display. */
    url?: string;
    /** Projection of the room's YouTube activity; its existing playlist owns selection. */
    activityManaged?: boolean;
}

export interface StageMediaHLSSource {
    kind: 'hls';
    streamURL: string;
    /** Matches livestream_channels.channel_id when tuned from the EPG. */
    channelId?: string;
    title?: string;
    logoUrl?: string;
    category?: string;
    language?: string;
}

export interface StageMediaMP4Source {
    kind: 'mp4';
    url: string;
    /** Equivalent VP8/Opus copy for generated clips on browsers without H.264. */
    webmUrl?: string;
    title?: string;
}

export type StageMediaSource =
    | StageMediaYouTubeSource
    | StageMediaHLSSource
    | StageMediaMP4Source;

export interface StageMediaQueueEntry {
    /** Stable key for list operations; independent from source identity. */
    id: string;
    source: StageMediaSource;
    /** Session id of the participant who enqueued this entry. */
    addedBy: string;
    addedAt: number;
}

export interface StageMediaPlaybackState {
    isPlaying: boolean;
    /** Optional shared YouTube audio controls. Device audio focus still takes precedence. */
    muted?: boolean;
    volume?: number;
    /** Transport position for youtube/mp4. Undefined for hls (live). */
    positionSec?: number;
    lastControlBy?: string;
    lastControlAt?: number;
}

export interface StageMediaState {
    current: StageMediaSource | null;
    currentStartedAt: number | null;
    /** Server-issued source incarnation. Stable across transport controls, renewed on each selection. */
    sourceEpoch?: string | null;
    /** HLS always yields an empty queue; server rejects queue writes for hls. */
    queue: readonly StageMediaQueueEntry[];
    /** -1 when current is null. */
    currentIndex: number;
    playback: StageMediaPlaybackState;
    /**
     * Last HLS seek-relative operation, so a client that missed the transient
     * `stage-media:seek-relative` event (e.g. socket disconnected during the
     * rewind) can still re-apply the same delta on the next state update.
     * MP4 seek-relative already mutates `playback.positionSec`, so this field
     * is only populated for HLS. Cleared on any source change, pause/play, or
     * absolute seek.
     */
    lastHlsSeekRelative?: {
        nonce: string;
        deltaSec: number;
        appliedAt: number;
    } | null;
}

export const MAX_STAGE_MEDIA_QUEUE = 5;

/** Kinds that support a host-curated queue. HLS is single-source (tune/stop). */
export const QUEUEABLE_KINDS: readonly StageMediaKind[] = ['youtube', 'mp4'];

/** Kinds that support play/pause transport (synchronized across participants). */
export const TRANSPORTABLE_KINDS: readonly StageMediaKind[] = ['youtube', 'hls', 'mp4'];

/** Kinds that support seek transport. HLS is live-only — seek is rejected. */
export const SEEKABLE_KINDS: readonly StageMediaKind[] = ['youtube', 'mp4'];

/** Canonical empty state — used by stores when a room has never had media. */
export const EMPTY_STAGE_MEDIA_STATE: StageMediaState = {
    current: null,
    currentStartedAt: null,
    queue: [],
    currentIndex: -1,
    playback: { isPlaying: false },
};

export const isQueueableKind = (kind: StageMediaKind): boolean =>
    QUEUEABLE_KINDS.includes(kind);

export const isTransportableKind = (kind: StageMediaKind): boolean =>
    TRANSPORTABLE_KINDS.includes(kind);

export const isSeekableKind = (kind: StageMediaKind): boolean =>
    SEEKABLE_KINDS.includes(kind);
